import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import Button from './Button';
import { parseDateInput, isoToDisplay, todayDisplay, DateInputField } from '../utils/dateInput';

const SUFFIXES = ['', 'Jr', 'Sr', 'II', 'III', 'IV'];
const SEX_OPTIONS = ['M', 'F'];

export default function AddEditPatientModal({ onClose, onSaved, patient = null, providers = [] }) {
  const isEdit = !!patient;
  const mrnCheckTimer = useRef(null);
  const dxSearchRef = useRef(null);

  const [form, setForm] = useState({
    first_name: patient?.first_name || '',
    middle_initial: patient?.middle_initial || '',
    last_name: patient?.last_name || '',
    suffix: patient?.suffix || '',
    date_of_birth: patient?.date_of_birth ? isoToDisplay(patient.date_of_birth) : '',
    sex: patient?.sex || '',
    mrn: patient?.mrn || '',
    provider_id: patient?.provider_id || (providers.length === 1 ? providers[0].id : ''),
    consult_date: '', // Optional — creates a visit record with is_consult=true on save
  });

  const [diagnoses, setDiagnoses] = useState(
    patient?.diagnoses?.map(d => ({
      id: d.id,
      snomed_id: d.snomed_id,
      diagnosis_role: d.diagnosis_role,
      notes: d.notes || '',
      display_name: d.snomed_code?.display_name || '',
    })) || []
  );

  const [snomedCodes, setSnomedCodes] = useState([]);
  const [mrnStatus, setMrnStatus] = useState(null);
  const [dxSearch, setDxSearch] = useState('');
  const [dxDropdownOpen, setDxDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState('demographics');

  useEffect(() => {
    api.get('/reference/snomed-codes').then(res => {
      setSnomedCodes(res.data);
    }).catch(() => { });
  }, []);

  const checkMrn = (mrn) => {
    if (!mrn || mrn === patient?.mrn) { setMrnStatus(null); return; }
    setMrnStatus('checking');
    clearTimeout(mrnCheckTimer.current);
    mrnCheckTimer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ mrn });
        if (patient?.id) params.set('exclude_patient_id', patient.id);
        const res = await api.get(`/patients/mrn-check?${params}`);
        if (res.data.available) {
          setMrnStatus('available');
        } else {
          setMrnStatus({ conflict: res.data.conflict_patient_name });
        }
      } catch { setMrnStatus(null); }
    }, 400);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'mrn') checkMrn(value);
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const filteredSnomed = dxSearch.trim()
    ? snomedCodes.filter(c =>
      c.display_name.toLowerCase().includes(dxSearch.toLowerCase()) ||
      (c.icd10_equivalent || '').toLowerCase().includes(dxSearch.toLowerCase())
    )
    : snomedCodes;

  const hasPrimary = diagnoses.some(d => d.diagnosis_role === 'primary');

  const addDiagnosis = (code) => {
    if (diagnoses.some(d => d.snomed_id === code.id)) return;
    const role = !hasPrimary ? 'primary' : 'contributing';
    setDiagnoses(prev => [...prev, {
      snomed_id: code.id,
      diagnosis_role: role,
      notes: '',
      display_name: code.display_name,
    }]);
    setDxSearch('');
    setDxDropdownOpen(false);
  };

  const removeDiagnosis = (index) => {
    setDiagnoses(prev => prev.filter((_, i) => i !== index));
  };

  const setPrimaryDiagnosis = (index) => {
    setDiagnoses(prev => prev.map((d, i) => ({
      ...d,
      diagnosis_role: i === index ? 'primary' : 'contributing',
    })));
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'Required';
    if (!form.last_name.trim()) errs.last_name = 'Required';
    if (!form.date_of_birth || !form.date_of_birth.trim()) {
      errs.date_of_birth = 'Required';
    } else if (!parseDateInput(form.date_of_birth)) {
      errs.date_of_birth = 'Enter as M/D/YY or M/D/YYYY';
    }
    if (!form.sex) errs.sex = 'Required';
    if (!form.mrn.trim()) errs.mrn = 'Required';
    if (!form.provider_id) errs.provider_id = 'Required';
    if (mrnStatus?.conflict) errs.mrn = `MRN already assigned to ${mrnStatus.conflict}`;
    if (diagnoses.length > 0 && !hasPrimary) {
      errs.diagnoses = 'One diagnosis must be marked as primary';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      if (errors.first_name || errors.last_name || errors.date_of_birth ||
        errors.sex || errors.mrn || errors.provider_id) {
        setActiveSection('demographics');
      } else if (errors.diagnoses) {
        setActiveSection('diagnoses');
      }
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        // Edit: update demographics and diagnoses
        await api.patch(`/patients/${patient.id}`, {
          first_name: form.first_name,
          middle_initial: form.middle_initial,
          last_name: form.last_name,
          suffix: form.suffix,
          date_of_birth: parseDateInput(form.date_of_birth)?.iso,
          sex: form.sex,
          mrn: form.mrn,
          provider_id: parseInt(form.provider_id),
        });
        for (const existing of patient.diagnoses || []) {
          try { await api.delete(`/patients/${patient.id}/diagnoses/${existing.id}`); } catch { }
        }
        for (const dx of diagnoses) {
          await api.post(`/patients/${patient.id}/diagnoses`, {
            snomed_id: dx.snomed_id,
            diagnosis_role: dx.diagnosis_role,
            notes: dx.notes || null,
          });
        }
        onSaved(); // edit — no ID, triggers list refresh

      } else {
        // Create: post patient, then create consult visit if date provided
        const res = await api.post('/patients/', {
          first_name: form.first_name,
          middle_initial: form.middle_initial,
          last_name: form.last_name,
          suffix: form.suffix,
          date_of_birth: parseDateInput(form.date_of_birth)?.iso,
          sex: form.sex,
          mrn: form.mrn,
          provider_id: parseInt(form.provider_id),
          diagnoses: diagnoses.map(d => ({
            snomed_id: d.snomed_id,
            diagnosis_role: d.diagnosis_role,
            notes: d.notes || null,
          })),
          allergies: [],
        });

        const newPatientId = res.data.id;

        // If a consult date was provided, create the visit record
        if (form.consult_date) {
          const parsedConsult = parseDateInput(form.consult_date);
          if (parsedConsult) {
            await api.post(`/patients/${newPatientId}/visits`, {
              visit_date: parsedConsult.iso,
              is_consult: true,
            });
          }
        }
        onSaved(newPatientId); // create — navigates to new chart
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('MRN')) {
        setErrors(prev => ({ ...prev, mrn: detail }));
        setActiveSection('demographics');
      } else {
        setErrors(prev => ({ ...prev, _general: 'Failed to save patient. Please try again.' }));
      }
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: 'demographics', label: 'Demographics' },
    { id: 'diagnoses', label: 'Diagnoses' },
  ];

  const sectionHasError = (id) => {
    if (id === 'demographics') return !!(errors.first_name || errors.last_name ||
      errors.date_of_birth || errors.sex || errors.mrn || errors.provider_id);
    if (id === 'diagnoses') return !!errors.diagnoses;
    return false;
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{isEdit ? 'Edit Patient' : 'Add New Patient'}</h2>
          <button onClick={onClose} style={styles.closeBtn}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={styles.sectionTabs}>
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              style={{ ...styles.sectionTab, ...(activeSection === s.id ? styles.sectionTabActive : {}) }}>
              {s.label}
              {sectionHasError(s.id) && <span style={styles.errorDot} />}
            </button>
          ))}
        </div>

        {errors._general && <div style={styles.generalError}>{errors._general}</div>}

        <div style={styles.modalBody}>

          {/* ── DEMOGRAPHICS ── */}
          {activeSection === 'demographics' && (
            <div style={styles.section}>
              <div style={styles.formRow}>
                <Field label="First Name *" error={errors.first_name}>
                  <input style={fieldStyle(errors.first_name)} value={form.first_name}
                    onChange={e => handleFormChange('first_name', e.target.value)} autoFocus />
                </Field>
                <Field label="MI" style={{ maxWidth: '60px' }}>
                  <input style={{ ...styles.input, maxWidth: '60px' }} value={form.middle_initial}
                    maxLength={1} onChange={e => handleFormChange('middle_initial', e.target.value)} />
                </Field>
                <Field label="Last Name *" error={errors.last_name}>
                  <input style={fieldStyle(errors.last_name)} value={form.last_name}
                    onChange={e => handleFormChange('last_name', e.target.value)} />
                </Field>
                <Field label="Suffix" style={{ maxWidth: '80px' }}>
                  <select style={{ ...styles.input, maxWidth: '80px' }} value={form.suffix}
                    onChange={e => handleFormChange('suffix', e.target.value)}>
                    {SUFFIXES.map(s => <option key={s} value={s}>{s || '—'}</option>)}
                  </select>
                </Field>
              </div>
              <div style={styles.formRow}>
                <Field label="Date of Birth *" error={errors.date_of_birth}>
                  <DateInputField
                    value={form.date_of_birth}
                    onChange={(display) => handleFormChange('date_of_birth', display)}
                    error={errors.date_of_birth}
                  />
                </Field>
                <Field label="Sex *" error={errors.sex}>
                  <select style={fieldStyle(errors.sex)} value={form.sex}
                    onChange={e => handleFormChange('sex', e.target.value)}>
                    <option value="">— Select —</option>
                    {SEX_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <div style={styles.formRow}>
                <Field label="MRN *" error={errors.mrn}>
                  <div style={{ position: 'relative' }}>
                    <input style={fieldStyle(errors.mrn)} value={form.mrn}
                      onChange={e => handleFormChange('mrn', e.target.value)} placeholder="Enter MRN" />
                    {mrnStatus === 'checking' && <span style={styles.mrnStatus}>Checking...</span>}
                    {mrnStatus === 'available' && <span style={{ ...styles.mrnStatus, color: '#059669' }}>✓ Available</span>}
                    {mrnStatus?.conflict && <span style={{ ...styles.mrnStatus, color: '#dc2626' }}>Already assigned to {mrnStatus.conflict}</span>}
                  </div>
                </Field>
                <Field label="Assigned Provider *" error={errors.provider_id}>
                  <select style={fieldStyle(errors.provider_id)} value={form.provider_id}
                    onChange={e => handleFormChange('provider_id', e.target.value)}>
                    <option value="">— Select Provider —</option>
                    {providers.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.last_name}, {p.first_name}{p.credential ? ` ${p.credential}` : ''}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              {/* Initial Consult Date — add mode only */}
              {!isEdit && (
                <div style={styles.consultSection}>
                  <div style={styles.consultSectionLabel}>Initial Consult Date</div>
                  <div style={styles.consultSectionHint}>
                    Optional — records the date of first nephrology visit. Can be entered or updated later from the patient chart.
                  </div>
                  <div style={styles.consultDateRow}>
                    <div style={styles.consultDateField}>
                      <DateInputField
                        value={form.consult_date}
                        onChange={(display) => handleFormChange('consult_date', display)}
                      />
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleFormChange('consult_date', todayDisplay())}
                    >
                      Today
                    </Button>
                    {form.consult_date && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFormChange('consult_date', '')}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── DIAGNOSES ── */}
          {activeSection === 'diagnoses' && (
            <div style={styles.section}>
              <p style={styles.sectionHint}>
                A <strong>primary</strong> diagnosis is recommended but not required at registration.
                Diagnoses can be added or updated at any time from the patient chart.
              </p>

              {diagnoses.length > 0 && (
                <div style={styles.dxList}>
                  {diagnoses.map((dx, i) => (
                    <div key={i} style={styles.dxRow}>
                      <div style={styles.dxInfo}>
                        <span style={{
                          ...styles.roleBadge,
                          ...(dx.diagnosis_role === 'primary' ? styles.rolePrimary : styles.roleContributing),
                        }}>
                          {dx.diagnosis_role === 'primary' ? 'Primary' : 'Contributing'}
                        </span>
                        <span style={styles.dxName}>{dx.display_name}</span>
                      </div>
                      <div style={styles.dxActions}>
                        {dx.diagnosis_role !== 'primary' && (
                          <Button variant="ghost" size="sm" onClick={() => setPrimaryDiagnosis(i)}>
                            Set Primary
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => removeDiagnosis(i)}
                          style={{ color: '#dc2626' }}>
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {errors.diagnoses && <div style={styles.fieldError}>{errors.diagnoses}</div>}

              <div style={styles.dxSearchArea}>
                <input
                  ref={dxSearchRef}
                  style={styles.input}
                  placeholder="Search diagnosis by name or ICD-10 code..."
                  value={dxSearch}
                  onChange={e => { setDxSearch(e.target.value); setDxDropdownOpen(true); }}
                  onFocus={() => setDxDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setDxDropdownOpen(false), 150)}
                />
                {dxDropdownOpen && dxSearch && (() => {
                  const rect = dxSearchRef.current?.getBoundingClientRect();
                  if (!rect) return null;
                  return (
                    <div style={{
                      ...styles.dxDropdown,
                      top: rect.bottom + 2,
                      left: rect.left,
                      width: rect.width,
                    }}>
                      {filteredSnomed.length === 0
                        ? <div style={styles.dxDropdownEmpty}>No diagnoses found</div>
                        : filteredSnomed.slice(0, 20).map(code => (
                          <button key={code.id} style={styles.dxDropdownItem}
                            onMouseDown={() => addDiagnosis(code)}>
                            <span style={styles.dxItemName}>{code.display_name}</span>
                            <span style={styles.dxItemCode}>{code.icd10_equivalent}</span>
                          </button>
                        ))
                      }
                    </div>
                  );
                })()}
                <p style={styles.dxHint}>
                  {!hasPrimary
                    ? 'First diagnosis added will be set as primary.'
                    : 'Additional diagnoses are added as contributing by default.'}
                </p>
              </div>
            </div>
          )}

        </div>

        <div style={styles.modalFooter}>
          <div style={styles.footerNav}>
            {activeSection !== 'demographics' && (
              <Button variant="secondary" onClick={() => setActiveSection('demographics')}>
                ← Back
              </Button>
            )}
          </div>
          <div style={styles.footerActions}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            {activeSection === 'demographics' ? (
              <>
                {isEdit && (
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
                <Button
                  variant={isEdit ? 'secondary' : 'primary'}
                  onClick={() => setActiveSection('diagnoses')}
                >
                  Next →
                </Button>
              </>
            ) : (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Patient'}
              </Button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

function Field({ label, error, children, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1, ...style }}>
      <label style={{
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--color-text-secondary)',
        fontFamily: 'var(--font-family-base)',
      }}>
        {label}
      </label>
      {children}
      {error && <span style={styles.fieldError}>{error}</span>}
    </div>
  );
}

const fieldStyle = (hasError) => ({
  width: '100%',
  padding: '8px 10px',
  border: `1px solid ${hasError ? '#dc2626' : 'var(--color-border)'}`,
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-primary)',
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-bg-card)',
  fontFamily: 'var(--font-family-base)',
});

const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', width: '640px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-lg)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0' },
  modalTitle: { fontSize: '18px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', margin: 0, fontFamily: 'var(--font-family-base)' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '4px', display: 'flex' },
  sectionTabs: { display: 'flex', padding: '16px 24px 0', borderBottom: '1px solid var(--color-border)', marginTop: '16px' },
  sectionTab: { padding: '8px 16px', backgroundColor: 'transparent', border: 'none', borderBottom: '2px solid transparent', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', cursor: 'pointer', marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'var(--font-family-base)' },
  sectionTabActive: { color: 'var(--color-primary)', borderBottom: '2px solid var(--color-primary)' },
  errorDot: { width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#dc2626', display: 'inline-block' },
  generalError: { margin: '12px 24px 0', padding: '10px 14px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', fontSize: '13px', color: '#991b1b', fontFamily: 'var(--font-family-base)' },
  modalBody: { flex: 1, padding: '20px 24px', overflowY: 'auto' },
  modalFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderTop: '1px solid var(--color-border)' },
  footerNav: { display: 'flex' },
  footerActions: { display: 'flex', gap: '10px' },
  section: { display: 'flex', flexDirection: 'column', gap: '16px' },
  sectionHint: { fontSize: '13px', color: 'var(--color-text-muted)', margin: 0, fontFamily: 'var(--font-family-base)' },
  formRow: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box', backgroundColor: 'var(--color-bg-card)', fontFamily: 'var(--font-family-base)' },
  fieldError: { fontSize: '11px', color: '#dc2626', marginTop: '2px', fontFamily: 'var(--font-family-base)' },
  mrnStatus: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'var(--color-text-muted)', pointerEvents: 'none', fontFamily: 'var(--font-family-base)' },
  consultSection: { display: 'flex', flexDirection: 'column', gap: '6px', padding: '14px', backgroundColor: 'var(--color-bg-subtle)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)' },
  consultSectionLabel: { fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
  consultSectionHint: { fontSize: '11px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', lineHeight: '1.5' },
  consultDateRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  consultDateField: { width: '200px', flexShrink: 0 },
  dxList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  dxRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: 'var(--color-bg-subtle)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', gap: '12px' },
  dxInfo: { display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 },
  dxName: { fontSize: '13px', color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-family-base)' },
  dxActions: { display: 'flex', gap: '4px', flexShrink: 0 },
  roleBadge: { padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: '11px', fontWeight: 'var(--font-weight-medium)', flexShrink: 0 },
  rolePrimary: { backgroundColor: '#dbeafe', color: '#1e40af' },
  roleContributing: { backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-muted)' },
  dxSearchArea: { display: 'flex', flexDirection: 'column', gap: '6px' },
  dxDropdown: { position: 'fixed', backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-lg)', zIndex: 9999, maxHeight: '240px', overflowY: 'auto' },
  dxDropdownEmpty: { padding: '12px 14px', fontSize: '13px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  dxDropdownItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '9px 14px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '12px', borderBottom: '1px solid var(--color-border)' },
  dxItemName: { fontSize: '13px', color: 'var(--color-text-primary)', flex: 1, fontFamily: 'var(--font-family-base)' },
  dxItemCode: { fontSize: '11px', color: 'var(--color-text-muted)', flexShrink: 0, fontFamily: 'var(--font-family-base)' },
  dxHint: { fontSize: '11px', color: 'var(--color-text-muted)', margin: 0, fontFamily: 'var(--font-family-base)' },
};